<template>
  <!-- One group so banners animate in and out; empty:hidden drops the margins when none shows -->
  <!-- Always present so screen readers pick up the banner when it appears (a region inserted already filled is often skipped) -->
  <p class="sr-only" role="status">{{ liveText }}</p>
  <TransitionGroup tag="div" v-bind="bannerTransition" class="m-4 flex flex-col gap-2.5 empty:hidden">
    <!-- No credits left -->
    <div v-if="showNoCredits" key="empty" :class="[bannerClass, tone.danger.banner]">
      <span :class="[chipClass, tone.danger.chip]">
        <Icon name="lucide:alert-triangle" class="h-4 w-4" aria-hidden="true" />
      </span>
      <div class="flex min-w-0 flex-col gap-px">
        <p :class="titleClass">{{ $t("common.no_credits_left") }}</p>
        <p v-if="user.next_reset_credit && user.planState !== 'lifetime'" :class="textClass">
          {{ $t("common.wait_until_reset") }}:
          <strong class="font-semibold text-foreground">{{ formatDate(user.next_reset_credit) }}</strong>
        </p>
      </div>
      <button type="button" :class="[actionClass, tone.danger.action]" @click="openPayPerUse('banner_empty')">
        <Icon name="lucide:coins" class="h-[15px] w-[15px]" aria-hidden="true" />
        {{ $t("buttons.purchaseCredits") }}
      </button>
      <button type="button" :class="[closeClass, tone.danger.close]" :aria-label="$t('buttons.close')" @click="hideNoCreditsAlert">
        <Icon name="lucide:x" class="h-[15px] w-[15px]" aria-hidden="true" />
      </button>
    </div>

    <!-- Running low on credits (10% of the plan's allowance, bonus included) -->
    <div v-if="showLowCredits" key="low" :class="[bannerClass, tone.warn.banner]">
      <span :class="[chipClass, tone.warn.chip]">
        <Icon name="lucide:alert-circle" class="h-4 w-4" aria-hidden="true" />
      </span>
      <div class="flex min-w-0 flex-col gap-px">
        <p :class="titleClass">{{ $t("common.low_credits_warning") }}</p>
        <p :class="textClass">
          {{ $t("common.credits_remaining", { count: formatNumber(totalCredits, locale) }) }}
          <template v-if="user.next_reset_credit && user.planState !== 'lifetime'">
            · {{ $t("common.next_reset_on") }}:
            <strong class="font-semibold text-foreground">{{ formatDate(user.next_reset_credit) }}</strong>
          </template>
        </p>
      </div>
      <button type="button" :class="[actionClass, tone.warn.action]" @click="openPayPerUse('banner_low')">
        <Icon name="lucide:coins" class="h-[15px] w-[15px]" aria-hidden="true" />
        {{ $t("buttons.purchaseCredits") }}
      </button>
      <button type="button" :class="[closeClass, tone.warn.close]" :aria-label="$t('buttons.close')" @click="hideLowCreditsAlert">
        <Icon name="lucide:x" class="h-[15px] w-[15px]" aria-hidden="true" />
      </button>
    </div>
  </TransitionGroup>
</template>

<script setup lang="ts">
import { useProModal } from "@/store/useProModal";
import { MONTHLY_LIMIT } from "~/constants/credits";
import { TRIAL_CREDITS } from "~/constants/trial";
import { formatNumber } from "~/utils/tools";

// "Running low" means 10% or less of the allowance the user started from:
// the trial grant for trials, the monthly pool otherwise (Lifetime included,
// whose 50,000 credits never refill, so the same absolute floor applies).
const LOW_CREDIT_RATIO = 0.1;

const { user } = useUser();
const { locale, t } = useI18n();
const proModal = useProModal();
const { trackUpsell } = useUpsellTracking();
const hideNoCreditsNotification = ref(false);
const hideLowCreditsNotification = ref(false);

const totalCredits = computed(
  () => (Number(user.value?.monthly_limit) || 0) + (Number(user.value?.bonus_limit) || 0)
);

const lowCreditThreshold = computed(() =>
  Math.round(
    (user.value?.planState === "trial" ? TRIAL_CREDITS : MONTHLY_LIMIT) *
      LOW_CREDIT_RATIO
  )
);

const showNoCredits = computed(
  () => !!user.value?.isPro && totalCredits.value === 0 && !hideNoCreditsNotification.value
);

const showLowCredits = computed(
  () =>
    !!user.value?.isPro &&
    totalCredits.value > 0 &&
    totalCredits.value <= lowCreditThreshold.value &&
    !hideLowCreditsNotification.value
);

// Dismissing the low-credit warning lasts until the next credit reset (the
// key includes the reset date), so it does not come back on every page load.
const lowDismissKey = computed(
  () => `low-credits-dismissed:${user.value?.next_reset_credit || "none"}`
);

onMounted(() => {
  try {
    hideLowCreditsNotification.value = localStorage.getItem(lowDismissKey.value) === "1";
  } catch {
    // Storage unavailable: show the warning; dismissal lasts for this view.
  }
});

// Polite announcement of the banner title; cleared first so a re-shown banner is read again
const liveText = ref("");
watch([showNoCredits, showLowCredits], ([empty, low]) => {
  const text = empty ? t("common.no_credits_left") : low ? t("common.low_credits_warning") : "";
  liveText.value = "";
  if (text) nextTick(() => (liveText.value = text));
});

watch(showNoCredits, (shown) => {
  if (shown) trackUpsell("credit_block", { feature: "banner_empty" });
}, { immediate: true });
watch(showLowCredits, (shown) => {
  if (shown) trackUpsell("credit_block", { feature: "banner_low" });
}, { immediate: true });

// Format in the app locale (not the browser's) so it matches the rest of the UI
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat(locale.value, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

// Open pay per use modal
const openPayPerUse = (feature: string) => {
  trackUpsell("gate_click", { feature, modal: "payPerUse" });
  proModal.onOpen("payPerUse", { feature });
};

// Hide no credits alert
const hideNoCreditsAlert = () => {
  hideNoCreditsNotification.value = true;
};

// Hide low credits alert
const hideLowCreditsAlert = () => {
  hideLowCreditsNotification.value = true;
  try {
    localStorage.setItem(lowDismissKey.value, "1");
  } catch {
    // Storage unavailable: hide for this page view only.
  }
};

// Below sm the action drops to its own full-width row; the close button stays pinned top-end.
const bannerClass =
  "relative grid grid-cols-[auto_1fr_auto] items-center gap-3 overflow-hidden rounded-md border px-3.5 py-[9px] shadow-sm sm:grid-cols-[auto_1fr_auto_auto]";
const chipClass = "grid h-7 w-7 flex-none place-items-center rounded-sm";
const titleClass = "text-[13.5px] font-semibold leading-[1.35] text-foreground";
const textClass = "text-[12.5px] leading-normal text-muted-foreground";
const actionClass =
  "col-span-full mt-1 inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-sm px-3.5 py-[7px] text-[12.5px] font-semibold transition hover:brightness-[1.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:col-span-1 sm:mt-0 sm:w-auto [@media(pointer:coarse)]:min-h-11";
const closeClass =
  "col-start-3 row-start-1 grid h-[26px] w-[26px] place-items-center rounded-sm text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:col-start-4 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11";

// Status Tint Rule (DESIGN.md): status color on a 10% tint with a ~25% border.
const tone = {
  danger: {
    banner: "border-destructive/25 bg-destructive/10",
    chip: "bg-destructive/15 text-destructive",
    action: "bg-destructive text-destructive-foreground",
    close: "hover:bg-destructive/10 hover:text-destructive",
  },
  warn: {
    banner: "border-warning/25 bg-warning/10",
    chip: "bg-warning/15 text-warning",
    action: "bg-warning text-warning-foreground",
    close: "hover:bg-warning/10 hover:text-warning",
  },
};

const bannerTransition = {
  enterActiveClass: "transition duration-200 ease-out motion-reduce:transition-none",
  leaveActiveClass: "transition duration-200 ease-out motion-reduce:transition-none",
  enterFromClass: "-translate-y-2 opacity-0",
  leaveToClass: "-translate-y-2 opacity-0",
};
</script>

